/**
 * DelegationDetailPage — J7-W3 task 3a (PRD §12.3.10)
 *
 * Read-only view of a single delegation. Shows all fields including the
 * raw permissions jsonb (key list), the linked Decision Note id (when set),
 * and the time window. The Revoke button is gated on status='active' +
 * the route's roros.actors.manage permission chain on the server side.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { t } from '@/lib/i18n';
import { RorosPage } from '@/components/roros/RorosPage';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import {
  useRorosDelegation,
  useRorosDelegationRevoke,
} from '@/hooks/roros/delegations';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import { formatDate } from '@/lib/localeFormatters';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';

function statusBadge(status: string): React.ReactNode {
  switch (status) {
    case 'active':
      return <Badge variant="success">{t('Active')}</Badge>;
    case 'expired':
      return <Badge variant="secondary">{t('Expired')}</Badge>;
    case 'revoked':
      return <Badge variant="error">{t('Revoked')}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export function DelegationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const delegationQuery = useRorosDelegation(id ?? '');
  const actorsQuery = useRorosActors();
  const domainsQuery = useRorosDomains();
  const revokeMutation = useRorosDelegationRevoke();

  const actors = (actorsQuery.data as RorosActorDTO[] | undefined) ?? [];
  const domains = (domainsQuery.data as RorosDomainDTO[] | undefined) ?? [];

  if (delegationQuery.isLoading) return <LoadingState />;
  if (delegationQuery.isError || !delegationQuery.data) {
    return (
      <RorosPage title={t('Delegation')}>
        <EmptyState
          icon={<ShieldCheck className="h-6 w-6" aria-hidden />}
          title={t('Delegation not found')}
          description={t('This delegation may have been deleted or is in another organization.')}
          action={{
            label: t('Back to Delegations'),
            onClick: () => navigate('/roros/delegations'),
          }}
        />
      </RorosPage>
    );
  }

  const d = delegationQuery.data;
  const delegator = actors.find((a) => a.id === d.delegatorActorId);
  const delegate = actors.find((a) => a.id === d.delegateActorId);
  const domainNames = (d.domainIds ?? []).map((did) => domains.find((dom) => dom.id === did)?.name ?? did);
  const permissionKeys = Object.keys(d.permissions ?? {});

  const onRevoke = () => {
    revokeMutation.mutate(d.id, {
      onSuccess: () => {
        showToast.success(t('Delegation revoked'));
        setConfirmRevoke(false);
      },
      onError: (err: Error) => {
        showToast.error(err.message || t('Failed to revoke delegation'));
      },
    });
  };

  return (
    <RorosPage
      title={t('Delegation')}
      icon={<ShieldCheck className="h-5 w-5" aria-hidden />}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/roros/delegations')} leftIcon={<ArrowLeft className="h-4 w-4" />}>
            {t('Back')}
          </Button>
          {d.status === 'active' && (
            <Button
              variant="outline"
              onClick={() => setConfirmRevoke(true)}
              data-testid="delegation-detail-revoke-button"
            >
              {t('Revoke')}
            </Button>
          )}
        </div>
      }
    >
      <div className="mt-4 space-y-4">
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground">{t('Status')}</span>
              {statusBadge(d.status)}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">{t('Delegator')}</p>
                <p className="font-medium" data-testid="delegation-delegator-name">{delegator?.name ?? d.delegatorActorId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Delegate')}</p>
                <p className="font-medium" data-testid="delegation-delegate-name">{delegate?.name ?? d.delegateActorId}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Scope')}</p>
                <p className="font-medium capitalize">{d.scope}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Linked Decision Note')}</p>
                <p className="font-medium">{d.decisionNoteId ?? t('—')}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Starts')}</p>
                <p className="font-medium">{d.startsAt ? formatDate(new Date(d.startsAt)) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{t('Expires')}</p>
                <p className="font-medium">{d.expiresAt ? formatDate(new Date(d.expiresAt)) : '—'}</p>
              </div>
            </div>
            {domainNames.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('Domains')}</p>
                <div className="flex flex-wrap gap-2">
                  {domainNames.map((n) => (
                    <Badge key={n} variant="outline">{n}</Badge>
                  ))}
                </div>
              </div>
            )}
            {permissionKeys.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">{t('Permissions')}</p>
                <div className="flex flex-wrap gap-2">
                  {permissionKeys.map((k) => (
                    <Badge key={k} variant="outline">{k.replace(/_/g, ' ')}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        isOpen={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={onRevoke}
        title={t('Revoke this delegation?')}
        description={t('Revoking will immediately end the delegation. This cannot be undone (GOV-05 audit event recorded).')}
        variant="destructive"
        confirmText={t('Revoke')}
        isLoading={revokeMutation.isPending}
      />
    </RorosPage>
  );
}
